import { Position } from 'geojson';
import { atomFamily, selectorFamily } from 'recoil';
import { AssetState } from '../types/currentState';

export const assetStartTimeState = atomFamily<number | undefined, string>({
  key: 'app.assetStates.startTime',
  default: undefined,
});

export const assetStates = atomFamily<AssetState[] | undefined, string>({
  key: 'app.assetStates',
  default: undefined,
});

export const reversedAssetStates = selectorFamily<AssetState[] | undefined, string>({
  key: 'app.assetStates.reversed',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(assetStates(assetId));
      if (!states) return;
      return [...states].reverse();
    },
});

export const assetLngLatsState = selectorFamily<Position[], string>({
  key: 'app.currentAssetLatLngsState',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(reversedAssetStates(assetId));
      if (!states || !states.length) return [];
      return states.map((state) => [state.position_longitude, state.position_latitude]);
    },
});

export const currentAssetState = selectorFamily<AssetState | undefined, string>({
  key: 'app.currentAssetState',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(reversedAssetStates(assetId));
      if (!states || !states.length) return;
      return states[0];
    },
});

export const averageSpeedState = selectorFamily<string, string>({
  key: 'app.assetState.averageSpeed',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(assetStates(assetId))?.filter((state) => state.position_speed > 0);
      if (!states?.length) return 'N/A';

      const historicSpeed = states.reduce((prev, curr) => prev + curr.position_speed, 0);
      const averageSpeed = (historicSpeed / states.length).toFixed(2);
      return `${averageSpeed} Km/h`;
    },
});

export const distanceTraveledState = selectorFamily<string, string>({
  key: 'app.assetState.distanceTraveled',
  get:
    (assetId) =>
    ({ get }) => {
      const states = get(assetStates(assetId));
      const reversedStates = get(reversedAssetStates(assetId));
      if (!states?.length || !reversedStates) return 'N/A';
      let initialMileage: number | undefined = states[0].gnss_vehicle_mileage;
      if (!initialMileage) {
        initialMileage = states?.find((state) => state.gnss_vehicle_mileage > 0)?.gnss_vehicle_mileage;
      }
      let lastMileage: number | undefined = reversedStates[0].gnss_vehicle_mileage;
      if (!lastMileage) {
        lastMileage = reversedStates?.find((state) => state.gnss_vehicle_mileage > 0)?.gnss_vehicle_mileage;
      }
      if (!initialMileage || !lastMileage) return 'N/A';
      const distanceTraveled = +(lastMileage - initialMileage).toFixed(2);
      return `${distanceTraveled.toLocaleString()} Km`;
    },
});
